import React, { useMemo } from 'react';
import useSeo from '../hooks/useSeo';
import '../styles/pages/Legal.css';

function TermsConditions() {
  const seoConfig = useMemo(() => {
    const title = 'BloomNav Terms and Conditions | Technology Studio';
    const description =
      'Terms for working with BloomNav on custom software projects: scope, timelines, payment, ownership, and support.';
    return {
      title,
      description,
      canonicalPath: '/terms',
      ogType: 'article',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav'
    };
  }, []);

  useSeo(seoConfig);

  return (
    <div className="legal-page">
      <div className="legal-head">
        <span className="legal-kicker">Legal</span>
        <h1 className="legal-title">
          Terms and <em>conditions.</em>
        </h1>
        <p className="legal-sub">
          The ground rules for working with us on a custom software project, and for using this
          website and our client portal.
        </p>
      </div>

      <section className="legal-section">
        <h2 className="legal-section-title">1. About these terms</h2>
        <div>
          <p>
            These terms apply to your use of the BloomNav website and client portal, and to
            services provided by CLIVEXCI SYSTEMS LLP trading as BloomNav ("BloomNav," "we," "our,"
            or "us"). By enquiring, engaging us, or using our portal you accept these terms.
          </p>
          <p>
            Every project also has its own proposal, scope, or statement of work. Where a signed
            project agreement differs from these terms, the project agreement wins for that project.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">2. What we do</h2>
        <div>
          <p>
            We are a technology studio. We design, build, launch, and maintain custom software:
            web apps, mobile apps, SaaS platforms, AI features, integrations, and internal systems.
          </p>
          <p>
            We are engaged as an independent contractor. We are not your employee, agent, or joint
            venture partner, and we do not provide legal, tax, accounting, or regulatory advice. If
            your product operates in a regulated area, you are responsible for getting the licences
            and advice you need.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">3. Scope, plans, and changes</h2>
        <div>
          <h3 className="legal-sub-title">3.1 Agreed scope</h3>
          <p>
            Before work starts we agree a written scope: what we are building, the main screens and
            flows, the platforms it ships on, the price, and the expected timeline. Anything not in
            that scope is not included.
          </p>

          <h3 className="legal-sub-title">3.2 Change requests</h3>
          <p>
            You can ask for changes at any time. New features, redesigns, or added platforms are
            quoted separately and may move the timeline. We will confirm the cost and impact in
            writing before doing the work.
          </p>

          <h3 className="legal-sub-title">3.3 Estimates and timelines</h3>
          <p>
            Timelines assume timely feedback, approvals, content, and access from your side. Dates
            are targets based on the agreed scope, not guarantees. Delays caused by waiting on you,
            on a third party, or on an app store review will shift the schedule accordingly.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">4. How we work together</h2>
        <div>
          <p>You agree to:</p>
          <ul className="legal-list">
            <li>Name one person who can give feedback and approve decisions.</li>
            <li>Give us the access, accounts, content, and information we need to build the product.</li>
            <li>Review builds and respond to questions within a reasonable time.</li>
            <li>Hold the licences, accounts, and permissions your product requires.</li>
          </ul>
          <p>We agree to:</p>
          <ul className="legal-list">
            <li>Work to the agreed scope and keep you informed of progress.</li>
            <li>Share a working build regularly during development so you can see where things stand.</li>
            <li>Tell you promptly if something in the plan needs to change.</li>
            <li>Keep your material confidential.</li>
          </ul>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">5. Fees and payment</h2>
        <div>
          <h3 className="legal-sub-title">5.1 Project fees</h3>
          <p>
            Project fees, milestones, and the payment schedule are set out in your proposal. Work
            usually begins after the first payment is received. Unless stated otherwise, fees are
            exclusive of taxes, and third-party costs such as hosting, app store fees, licences, and
            paid APIs are billed to you or paid directly by you.
          </p>

          <h3 className="legal-sub-title">5.2 Ongoing work</h3>
          <p>
            Retainers and support arrangements are billed for the agreed period in advance unless we
            agree otherwise. Unused time in a retainer period does not roll over unless your
            agreement says it does.
          </p>

          <h3 className="legal-sub-title">5.3 Late payment</h3>
          <p>
            If an invoice is overdue we may pause work and withhold delivery of pending work until
            payment is made. We will give notice before pausing. Pausing for non-payment does not
            count as a delay on our side.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">6. Ownership and licences</h2>
        <div>
          <h3 className="legal-sub-title">6.1 Your material</h3>
          <p>
            You keep ownership of everything you give us: your brand, content, data, and existing
            systems. You grant us the right to use it for the duration of the project so we can do
            the work.
          </p>

          <h3 className="legal-sub-title">6.2 What we deliver</h3>
          <p>
            On full payment, ownership of the custom code, designs, and assets created specifically
            for your project transfers to you. Before full payment, the deliverables remain ours.
          </p>

          <h3 className="legal-sub-title">6.3 Our tools and know-how</h3>
          <p>
            We keep ownership of our own pre-existing tools, libraries, templates, internal
            components, and general know-how. Where those are embedded in your product, you receive
            a perpetual, non-exclusive licence to use them as part of the delivered work.
          </p>

          <h3 className="legal-sub-title">6.4 Third-party components</h3>
          <p>
            Products usually include open source or commercially licensed components. Those stay
            under their own licences, and any ongoing subscription costs are yours.
          </p>

          <h3 className="legal-sub-title">6.5 Portfolio use</h3>
          <p>
            Unless you ask us not to, we may mention that we worked with you and show non-confidential
            visuals of the work in our portfolio.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">7. Delivery, acceptance, and support</h2>
        <div>
          <p>
            We deliver work in stages and share builds during development. When a milestone is
            delivered you have a review window, stated in your proposal, to raise anything that does
            not match the agreed scope. We fix in-scope defects at no extra cost.
          </p>
          <p>
            If we do not hear from you within the review window, the milestone is treated as
            accepted. After launch, defects in the delivered scope are covered for the warranty
            period in your agreement. New features, changes of direction, and ongoing maintenance
            are handled under a separate support or retainer arrangement.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">8. Confidentiality</h2>
        <div>
          <p>
            Each of us will keep the other's confidential information private and use it only for
            the project. This does not cover information that is already public, that we knew before,
            or that we must disclose by law. These obligations continue after the project ends.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">9. Website and portal use</h2>
        <div>
          <p>When using our website or client portal you agree not to:</p>
          <ul className="legal-list">
            <li>Attempt to access accounts, data, or systems that are not yours.</li>
            <li>Probe, scan, or test the security of our systems without written permission.</li>
            <li>Upload malicious code or content that is unlawful or infringes someone's rights.</li>
            <li>Interfere with, overload, or disrupt the service.</li>
          </ul>
          <p>
            You are responsible for keeping your login details secure and for activity under your
            account. Tell us straight away if you suspect unauthorised access.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">10. Warranties and disclaimers</h2>
        <div>
          <p>
            We will perform our services with reasonable skill and care, in line with professional
            standards. Beyond that, our services and this website are provided as is. We do not
            warrant that software will be free of all defects, that it will meet outcomes we have not
            agreed in writing, or that third-party platforms, APIs, and app stores will remain
            available or unchanged.
          </p>
          <p>
            We are not responsible for problems caused by changes you or others make to the code or
            infrastructure after handover, or by third-party services outside our control.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">11. Limitation of liability</h2>
        <div>
          <p>
            To the extent the law allows, neither party is liable for indirect or consequential loss,
            including lost profits, lost revenue, lost data, or business interruption.
          </p>
          <p>
            Our total liability arising from a project is limited to the fees you paid us for that
            project in the twelve months before the claim. Nothing in these terms limits liability
            that cannot be limited by law, including for fraud or wilful misconduct.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">12. Suspension and termination</h2>
        <div>
          <p>
            Either of us may end an engagement with written notice as set out in the project
            agreement. If work ends early, you pay for work completed and costs committed up to that
            point, and we hand over the completed deliverables you have paid for.
          </p>
          <p>
            We may suspend or end an engagement immediately if invoices remain unpaid after notice,
            if the work would require us to break the law, or if the working relationship becomes
            unworkable, including abusive conduct toward our team.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">13. Governing law</h2>
        <div>
          <p>
            These terms are governed by the laws of India. The courts of Goa, India have exclusive
            jurisdiction over any dispute, unless your project agreement says otherwise. Before
            starting formal proceedings, both parties agree to try to resolve the issue in good faith
            through discussion.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">14. Changes to these terms</h2>
        <div>
          <p>
            We may update these terms from time to time. The date below shows the current version.
            Changes do not alter the terms of a project agreement already signed. Continuing to use
            our website or portal after an update means you accept the revised terms.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">15. Contact us</h2>
        <div>
          <p>Questions about these terms:</p>
          <p><strong>Company:</strong> CLIVEXCI SYSTEMS LLP (BloomNav)</p>
          <p><strong>Email:</strong> support@bloomnav.com</p>
          <p><strong>Subject line:</strong> Terms enquiry</p>
          <p><strong>Location:</strong> Goa, India</p>
        </div>
      </section>

      <div className="legal-stamp">
        <p><strong>Last updated:</strong> July 2026</p>
        <p><strong>Effective date:</strong> July 2026</p>
      </div>
    </div>
  );
}

export default TermsConditions;
